/**
 * SearchBar Component
 *
 * Reusable search input with debouncing and clear functionality
 */

import { useState, useEffect, useCallback, useRef, memo } from 'react';
import { TextInput, ActionIcon } from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import { useDebounce } from '../../../../hooks';
import type { SearchBarProps } from '../SearchAndFilters.types';
import styles from '../SearchAndFilters.module.css';

export const SearchBar = memo(function SearchBar({
  placeholder,
  value,
  onChange,
  onClear,
  size = 'sm'
}: SearchBarProps) {
  const [localSearchText, setLocalSearchText] = useState(value);
  const previousDebouncedValueRef = useRef<string>(value);

  const { debouncedValue: debouncedSearchText, cancel } = useDebounce(localSearchText, 300, {
    trailing: true,
    maxWait: 1000,
  });

  useEffect(() => {
    // Only call onChange if the debounced value actually changed
    if (debouncedSearchText !== previousDebouncedValueRef.current) {
      previousDebouncedValueRef.current = debouncedSearchText;
      onChange(debouncedSearchText);
    }
  }, [debouncedSearchText, onChange]);

  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  const handleSearchChange = useCallback((event: { target: { value: string } }) => {
    setLocalSearchText(event.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setLocalSearchText('');
    onChange('');
    onClear?.();
  }, [onChange, onClear]);

  return (
    <TextInput
      className={styles.searchInput}
      placeholder={placeholder}
      leftSection={<IconSearch size={16} />}
      rightSection={
        localSearchText && (
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={handleClearSearch}
            aria-label="Clear search"
            size="sm"
          >
            <IconX size={16} />
          </ActionIcon>
        )
      }
      value={localSearchText}
      onChange={handleSearchChange}
      autoComplete="off"
      size={size}
    />
  );
});
